/**
 * Telegram WebApp API utilities
 * Provides haptic feedback, back button, and other native features
 */

class TelegramWebApp {
  constructor() {
    this.tg = window.Telegram?.WebApp;
    this.isAvailable = !!this.tg;
  }

  /**
   * Initialize WebApp
   */
  init() {
    if (!this.isAvailable) return;
    this.tg.ready();
    this.tg.expand();
  }

  /**
   * Haptic feedback - light impact
   */
  hapticLight() {
    if (this.isAvailable && this.tg.HapticFeedback) {
      this.tg.HapticFeedback.impactOccurred('light');
    }
  }

  /**
   * Haptic feedback - medium impact
   */
  hapticMedium() {
    if (this.isAvailable && this.tg.HapticFeedback) {
      this.tg.HapticFeedback.impactOccurred('medium');
    }
  }

  /**
   * Haptic feedback - heavy impact
   */
  hapticHeavy() {
    if (this.isAvailable && this.tg.HapticFeedback) {
      this.tg.HapticFeedback.impactOccurred('heavy');
    }
  }

  /**
   * Haptic feedback - selection changed
   */
  hapticSelection() {
    if (this.isAvailable && this.tg.HapticFeedback) {
      this.tg.HapticFeedback.selectionChanged();
    }
  }

  /**
   * Haptic feedback - notification (success, error, warning)
   */
  hapticNotification(type = 'success') {
    if (this.isAvailable && this.tg.HapticFeedback) {
      this.tg.HapticFeedback.notificationOccurred(type);
    }
  }

  /**
   * Show BackButton and handle click
   */
  showBackButton(onClick) {
    if (!this.isAvailable || !this.tg.BackButton) return;

    this.tg.BackButton.show();
    this.tg.BackButton.onClick(onClick);
  }

  /**
   * Hide BackButton
   */
  hideBackButton() {
    if (!this.isAvailable || !this.tg.BackButton) return;
    this.tg.BackButton.hide();
  }

  /**
   * Show MainButton with text and handler
   */
  showMainButton(text, onClick, params = {}) {
    if (!this.isAvailable || !this.tg.MainButton) return;

    this.tg.MainButton.setText(text);

    if (params.color) this.tg.MainButton.color = params.color;
    if (params.textColor) this.tg.MainButton.textColor = params.textColor;

    this.tg.MainButton.show();
    this.tg.MainButton.onClick(onClick);

    if (params.progress) {
      this.tg.MainButton.showProgress();
    }
  }

  /**
   * Hide MainButton
   */
  hideMainButton() {
    if (!this.isAvailable || !this.tg.MainButton) return;
    this.tg.MainButton.hide();
  }

  /**
   * Enable MainButton
   */
  enableMainButton() {
    if (!this.isAvailable || !this.tg.MainButton) return;
    this.tg.MainButton.enable();
  }

  /**
   * Disable MainButton
   */
  disableMainButton() {
    if (!this.isAvailable || !this.tg.MainButton) return;
    this.tg.MainButton.disable();
  }

  /**
   * Show progress on MainButton
   */
  showMainButtonProgress() {
    if (!this.isAvailable || !this.tg.MainButton) return;
    this.tg.MainButton.showProgress();
  }

  /**
   * Hide progress on MainButton
   */
  hideMainButtonProgress() {
    if (!this.isAvailable || !this.tg.MainButton) return;
    this.tg.MainButton.hideProgress();
  }

  /**
   * Open link in external browser
   */
  openLink(url) {
    if (this.isAvailable) {
      this.tg.openLink(url);
    } else {
      window.open(url, '_blank');
    }
  }

  /**
   * Open Telegram link (user, chat, etc)
   */
  openTelegramLink(url) {
    if (this.isAvailable) {
      this.tg.openTelegramLink(url);
    } else {
      window.open(url, '_blank');
    }
  }

  /**
   * Show popup
   */
  showPopup(params) {
    if (this.isAvailable && this.tg.showPopup) {
      this.tg.showPopup(params);
    } else {
      // Fallback to alert
      alert(params.message);
    }
  }

  /**
   * Show confirm
   */
  showConfirm(message, callback) {
    if (this.isAvailable && this.tg.showConfirm) {
      this.tg.showConfirm(message, callback);
    } else {
      const result = window.confirm(message);
      if (callback) callback(result);
    }
  }

  /**
   * Close WebApp
   */
  close() {
    if (this.isAvailable) {
      this.tg.close();
    }
  }

  /**
   * Get user data
   */
  getUser() {
    if (this.isAvailable) {
      return this.tg.initDataUnsafe?.user;
    }
    return null;
  }

  /**
   * Get init data
   */
  getInitData() {
    if (this.isAvailable) {
      return this.tg.initData;
    }
    return null;
  }

  /**
   * Set header color
   */
  setHeaderColor(color) {
    if (this.isAvailable && this.tg.setHeaderColor) {
      this.tg.setHeaderColor(color);
    }
  }

  /**
   * Set background color
   */
  setBackgroundColor(color) {
    if (this.isAvailable && this.tg.setBackgroundColor) {
      this.tg.setBackgroundColor(color);
    }
  }

  /**
   * Request write access
   */
  requestWriteAccess(callback) {
    if (this.isAvailable && this.tg.requestWriteAccess) {
      this.tg.requestWriteAccess(callback);
    }
  }

  /**
   * Request contact
   */
  requestContact(callback) {
    if (this.isAvailable && this.tg.requestContact) {
      this.tg.requestContact(callback);
    }
  }
}

// Create singleton instance
const telegram = new TelegramWebApp();

export default telegram;
